import * as React from "react"
import { cn } from "@/lib/utils"
import { InfoIcon } from "lucide-react"
import {
  Tooltip,
} from "@/components/ui/tooltip"

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: React.ReactNode
  labelTooltip?: React.ReactNode
  description?: React.ReactNode
  error?: string | { message: string; match: string }
  size?: "xs" | "sm" | "md" | "lg"
  variant?: "default" | "error"
  /** Suffix shown next to the label when the field is optional. Defaults to "(optional)". Pass a localized string to translate it. */
  optionalLabel?: string
}

const inputSizes = {
  xs: "h-7 px-2 text-xs",
  sm: "h-8 px-2.5 text-sm",
  md: "h-9 px-3 text-base md:text-sm",
  lg: "h-10 px-4 text-base",
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      label,
      labelTooltip,
      description,
      error,
      size = "md",
      variant = "default",
      required,
      disabled,
      id: propsId,
      optionalLabel,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId()
    const id = propsId || generatedId
    const errorId = `${id}-error`
    const descriptionId = `${id}-description`

    // Determine if we should show error
    const hasError = variant === "error" || !!error
    const errorMessage = typeof error === "string" ? error : error?.message

    // Field wrapper content
    const inputElement = (
      <input
        type={type}
        id={id}
        ref={ref}
        disabled={disabled}
        required={required}
        aria-invalid={hasError}
        aria-describedby={
          hasError
            ? errorId
            : description
              ? descriptionId
              : undefined
        }
        className={cn(
          // Base styles
          "flex w-full min-w-0 rounded-md border bg-transparent shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
          
          // Size styles
          inputSizes[size],
          
          // Text and placeholder
          "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30",
          
          // Focus styles
          "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
          
          // Error or default styles
          hasError
            ? "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive"
            : "border-input",
          
          className
        )}
        {...props}
      />
    )

    // Bare input (no label)
    if (!label) {
      return inputElement
    }

    // Input with field wrapper
    return (
      <div className="flex flex-col gap-1.5">
        {/* Label */}
        <div className="flex items-center gap-2">
          <label
            htmlFor={id}
            className={cn(
              "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
              hasError && "text-destructive"
            )}
          >
            {label}
            {required === false && (
              <span className="ml-1 text-muted-foreground">{optionalLabel ?? "(optional)"}</span>
            )}
          </label>
          
          {labelTooltip && (
            <Tooltip content={labelTooltip}>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <InfoIcon className="h-4 w-4" />
              </button>
            </Tooltip>
          )}
        </div>

        {/* Input */}
        {inputElement}

        {/* Description or Error */}
        {(description || errorMessage) && (
          <div className={cn("text-sm", hasError ? "text-destructive" : "text-muted-foreground")}>
            {hasError ? (
              <span id={errorId} role="alert">
                {errorMessage}
              </span>
            ) : description ? (
              <span id={descriptionId}>{description}</span>
            ) : null}
          </div>
        )}
      </div>
    )
  }
)

Input.displayName = "Input"

export { Input }
